import { useQuery } from '@apollo/client';
import {
  GET_DIRECTLY_SUPPORTED_IT_APPLICATIONS_LOCATION_METRICS,
  GET_DIRECTLY_SUPPORTED_IT_APPLICATIONS_ENTERPRISE_METRICS,
} from '../graphql';
import type {
  GetDirectlySupportedItApplicationsLocationMetricsData,
  GetDirectlySupportedItApplicationsLocationMetricsVars,
  GetDirectlySupportedItApplicationsEnterpriseMetricsData,
  GetDirectlySupportedItApplicationsEnterpriseMetricsVars,
} from '../types';

/**
 * Hook to fetch directly supported revenue applications metrics
 * @param locationUuid - The UUID of the location to fetch the data for. If not provided, the data will be fetched for enterprise level.
 * @returns {{ directlySupportedApplications: number, loading: boolean, error: ApolloError }}
 */
export const useDirectlySupportedRevenueApplicationsMetrics = (
  locationUuid?: string,
) => {
  // Location-specific query
  const locationResult = useQuery<
    GetDirectlySupportedItApplicationsLocationMetricsData,
    GetDirectlySupportedItApplicationsLocationMetricsVars
  >(GET_DIRECTLY_SUPPORTED_IT_APPLICATIONS_LOCATION_METRICS, {
    variables: { data: locationUuid! },
    skip: !locationUuid,
    errorPolicy: 'all',
  });

  // Enterprise query
  const enterpriseResult = useQuery<
    GetDirectlySupportedItApplicationsEnterpriseMetricsData,
    GetDirectlySupportedItApplicationsEnterpriseMetricsVars
  >(GET_DIRECTLY_SUPPORTED_IT_APPLICATIONS_ENTERPRISE_METRICS, {
    variables: {
      data: {
        enterpriseWide: true,
      },
    },
    skip: !!locationUuid,
    errorPolicy: 'all',
  });

  // Use the appropriate result based on locationUuid
  const result = locationUuid ? locationResult : enterpriseResult;

  // Debug logging
  if (result.error) {
    console.error(
      'useDirectlySupportedRevenueApplicationsMetrics GraphQL error:',
      result.error,
    );
  }

  // Extract data from the appropriate query result
  const directlySupportedApplications = locationUuid
    ? locationResult.data?.getSelectedLocationItApplications?.length || 0
    : enterpriseResult.data?.getSelectedItApplications?.length || 0;

  return {
    directlySupportedApplications,
    loading: result.loading,
    error: result.error,
  };
};
